// テイクアウト注文画面コンポーネント
import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import { ShoppingCart, Plus, Minus, Clock, Phone, User } from 'lucide-react';import PageContainer from '../layout/PageContainer';




interface MenuItem {
  id: string;
  name: string;
  price: number;
  description: string;
  imageUrl?: string;
  available: boolean;
}

interface CartItem extends MenuItem {
  quantity: number;
}

interface CustomerInfo {
  name: string;
  phone: string;
  specialRequests?: string;
}

const TakeoutOrder: React.FC = () => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    phone: '',
    specialRequests: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDate, setPickupDate] = useState<'today' | 'tomorrow'>('today');
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [timeInfoMessage, setTimeInfoMessage] = useState('');
  const [isTodayDisabled, setIsTodayDisabled] = useState(false);
  const customerInfoStorageKey = 'waton_takeout_customer_info';

  const placeholderImage =
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='250'><rect width='100%25' height='100%25' fill='%23f3f4f6'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='20'>NO IMAGE</text></svg>";

  useEffect(() => {
    fetchMenuItems();
    const savedCustomerInfo = localStorage.getItem(customerInfoStorageKey);
    if (savedCustomerInfo) {
      try {
        const parsed = JSON.parse(savedCustomerInfo) as Partial<CustomerInfo>;
        setCustomerInfo(prev => ({
          ...prev,
          name: parsed.name || prev.name,
          phone: parsed.phone || prev.phone
        }));
      } catch (error) {
        console.warn('保存された顧客情報の読み込みに失敗しました', error);
      }
    }
    loadCustomerInfoFromLine();
  }, []);

  useEffect(() => {
    if (!showOrderForm) return;
    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(20, 30, 0, 0);
    const cutoffPassed = now > cutoff;
    setIsTodayDisabled(cutoffPassed);

    if (cutoffPassed && pickupDate !== 'tomorrow') {
      setPickupDate('tomorrow');
      return;
    }

    const { slots, infoMessage } = buildTimeSlots(pickupDate, now);
    setTimeSlots(slots);
    setTimeInfoMessage(infoMessage);

    if (pickupTime && !slots.includes(pickupTime)) {
      setPickupTime('');
    }
  }, [pickupDate, showOrderForm]);

  useEffect(() => {
    if (!customerInfo.name && !customerInfo.phone) return;
    localStorage.setItem(customerInfoStorageKey, JSON.stringify({
      name: customerInfo.name,
      phone: customerInfo.phone
    }));
  }, [customerInfo.name, customerInfo.phone]);

  const fetchMenuItems = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/takeout/menu`);
      const data = await response.json();
      setMenuItems(data.items || []);
    } catch (error) {
      console.error('メニュー取得エラー:', error);
      // デモデータ
      setMenuItems([
        {
          id: 'B001',
          name: 'ロースカツ弁当',
          price: 980,
          description: 'ジューシーなロースカツとご飯のセット',
          available: true
        },
        {
          id: 'B002',
          name: 'ヒレカツ弁当',
          price: 1080,
          description: '柔らかいヒレカツとご飯のセット',
          available: true
        },
        {
          id: 'B003',
          name: 'エビフライ弁当',
          price: 880,
          description: '大きなエビフライ2本とご飯のセット',
          available: true
        },
        {
          id: 'B004',
          name: 'ミックス弁当',
          price: 1180,
          description: 'ロースカツとエビフライの欲張りセット',
          available: true
        }
      ]);
    }
  };

  const loadCustomerInfoFromLine = async () => {
    try {
      const token = new URLSearchParams(window.location.search).get('token');
      if (token) {
        const response = await fetch(`${getApiUrl()}/line/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success && data.user) {
          setCustomerInfo(prev => ({
            ...prev,
            name: data.user.name || prev.name || '',
            phone: data.user.phoneNumber || prev.phone || ''
          }));
        }
      }
    } catch (error) {
      console.error('顧客情報取得エラー:', error);
    }
  };

  const roundUpToInterval = (date: Date, intervalMinutes: number) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const remainder = minutes % intervalMinutes;
    if (remainder !== 0) {
      rounded.setMinutes(minutes + (intervalMinutes - remainder));
    }
    rounded.setSeconds(0, 0);
    return rounded;
  };

  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const buildTimeSlots = (dateType: 'today' | 'tomorrow', now: Date) => {
    const interval = 15;
    const openHour = 11;
    const closeHour = 21;

    let startTime = new Date(now);
    startTime.setHours(openHour, 0, 0, 0);
    if (dateType === 'today') {
      const earliest = new Date(now.getTime() + 30 * 60000);
      if (earliest > startTime) {
        startTime = roundUpToInterval(earliest, interval);
      }
    }

    const endTime = new Date(now);
    endTime.setHours(closeHour, 0, 0, 0);

    if (startTime > endTime) {
      return {
        slots: [],
        infoMessage: '本日の受付は終了しました。翌日を選択してください。'
      };
    }

    const slots: string[] = [];
    let cursor = new Date(startTime);
    while (cursor <= endTime) {
      slots.push(formatTime(cursor));
      cursor = new Date(cursor.getTime() + interval * 60000);
    }

    return {
      slots,
      infoMessage: dateType === 'today'
        ? '本日受取の時間を選択してください（15分刻み）'
        : '明日受取の時間を選択してください（15分刻み）'
    };
  };

  const addToCart = (item: MenuItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id);
      if (existing) {
        return prev.map(c => 
          c.id === item.id 
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateQuantity = (itemId: string, change: number) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === itemId) {
          const newQuantity = item.quantity + change;
          return newQuantity <= 0 ? null : { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean) as CartItem[];
    });
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getPointsEarned = () => {
    return Math.floor(getTotalAmount() / 500);
  };

  const submitOrder = async () => {
    if (!pickupTime) {
      alert('受取時間を選択してください');
      return;
    }
    if (!customerInfo.name || !customerInfo.phone) {
      alert('お名前と電話番号を入力してください');
      return;
    }

    setLoading(true);
    try {
      const pickupTimeLabel = pickupDate === 'today' ? pickupTime : `明日 ${pickupTime}`;
      const orderData = {
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        specialRequests: customerInfo.specialRequests,
        orderItems: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        totalAmount: getTotalAmount(),
        paymentMethod: paymentMethod,
        pickupTime: pickupTimeLabel
      };

      const response = await fetch(`${getApiUrl()}/takeout/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();
      if (result.success) {
        setOrderNumber(result.orderNumber);
        setOrderComplete(true);
      }
    } catch (error) {
      console.error('注文送信エラー:', error);
      alert('注文の送信に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (orderComplete) {
    return (
      <PageContainer>
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🏮</div>
          <h2 className="text-2xl font-bold mb-4">ご注文ありがとうございます！</h2>
          <div className="bg-orange-50 rounded-lg p-4 mb-4">
            <p className="text-lg font-semibold">注文番号</p>
            <p className="text-3xl font-bold text-orange-600">{orderNumber}</p>
          </div>
          <p className="mb-6">
            ご注文の準備ができましたらLINEでお知らせします。<br />
            11:00〜21:00の間にお好きな時間にお越しください。
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg font-bold"
          >
            ホームへ戻る
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-orange-600 text-white p-4 sticky top-0 z-10">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold">🏮 和とん テイクアウト注文</h1>
          <button
            onClick={() => setShowCart(true)}
            className="relative bg-white text-orange-600 px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <ShoppingCart size={20} />
            <span className="font-bold">¥{getTotalAmount().toLocaleString()}</span>
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                {cart.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 注文受付時間の案内 */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4">
        <div className="flex items-center gap-2">
          <Clock className="text-yellow-600" />
          <div>
            <p className="font-bold">前日予約で待ち時間ゼロ！</p>
            <p className="text-sm">前日20:00〜当日9:00までのご注文で、11:00からいつでもお受け取りいただけます</p>
          </div>
        </div>
      </div>

      {/* メニュー一覧 */}
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">メニュー</h2>
        <div className="grid gap-4">
          {menuItems.map(item => (
            <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gray-100">
                <img
                  src={item.imageUrl || placeholderImage}
                  alt={item.name}
                  className="w-full h-40 object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = placeholderImage;
                  }}
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{item.name}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                    <p className="text-xl font-bold text-orange-600 mt-2">
                      ¥{item.price.toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => addToCart(item)}
                  disabled={!item.available}
                  className={`w-full py-2 rounded-lg font-bold transition ${
                    item.available
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {item.available ? 'カートに追加' : '売り切れ'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* カート画面 */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">カート</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-500"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {cart.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                カートは空です
              </div>
            ) : (
              <>
                <div className="p-4">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center gap-4 mb-4 pb-4 border-b">
                      <div className="flex-1">
                        <h4 className="font-bold">{item.name}</h4>
                        <p className="text-orange-600">¥{item.price.toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">¥{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                  
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span>小計</span>
                      <span className="font-bold">¥{getTotalAmount().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>獲得予定ポイント</span>
                      <span>{getPointsEarned()}pt</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowCart(false);
                      setShowOrderForm(true);
                    }}
                    className="w-full mt-4 bg-orange-500 text-white py-3 rounded-lg font-bold"
                  >
                    注文手続きへ進む
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 注文フォーム */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowOrderForm(false)}
                className="text-gray-500"
              >
                ←
              </button>
              <h2 className="text-xl font-bold">注文情報入力</h2>
            </div>
          </div>
          
          <div className="p-4">
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">
                <User className="inline w-4 h-4 mr-1" />
                お名前（必須）
              </label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="山田太郎"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">
                <Phone className="inline w-4 h-4 mr-1" />
                電話番号（必須）
              </label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="090-1234-5678"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">
                特別なご要望（任意）
              </label>
              <textarea
                value={customerInfo.specialRequests}
                onChange={(e) => setCustomerInfo({...customerInfo, specialRequests: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows={3}
                placeholder="アレルギーや特別な要望があればご記入ください"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">
                受取時間（必須）
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setPickupDate('today')}
                  disabled={isTodayDisabled}
                  className={`flex-1 py-2 rounded-lg font-bold ${
                    pickupDate === 'today'
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                  } ${isTodayDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  今日
                </button>
                <button
                  type="button"
                  onClick={() => setPickupDate('tomorrow')}
                  className={`flex-1 py-2 rounded-lg font-bold ${
                    pickupDate === 'tomorrow'
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  明日
                </button>
              </div>

              {isTodayDisabled && (
                <div className="text-sm text-red-600 mb-2">
                  本日の受付は終了しました。明日を選択してください。
                </div>
              )}

              {timeInfoMessage && (
                <div className="text-sm text-gray-600 mb-2">
                  {timeInfoMessage}
                </div>
              )}

              <select
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg bg-white"
                disabled={timeSlots.length === 0}
              >
                <option value="">受取時間を選択してください</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">
                お支払い方法
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-3"
                  />
                  <span>店頭現金払い（受取時）</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    value="paypay"
                    checked={paymentMethod === 'paypay'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-3"
                  />
                  <span>PayPay事前決済</span>
                </label>
              </div>
            </div>
            
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-bold mb-2">注文内容</h3>
              {cart.map(item => (
                <div key={item.id} className="flex justify-between text-sm mb-1">
                  <span>{item.name} × {item.quantity}</span>
                  <span>¥{(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold">
                  <span>合計</span>
                  <span className="text-orange-600">¥{getTotalAmount().toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={submitOrder}
              disabled={loading}
              className={`w-full py-3 rounded-lg font-bold ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white'
              }`}
            >
              {loading ? '送信中...' : '注文を確定する'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakeoutOrder;
